import styled from '@emotion/styled'
import { useEffect, useRef, useState } from 'react'
import PerfectScrollbar from 'react-perfect-scrollbar'
import { t } from 'i18next'
import { nanoid } from 'nanoid'
import {
  AuthorInitialNameStyle,
  ButtonAddStyle,
  ItemButtonStyle,
  MenuStyle,
} from '../SearchBarComponentsStyle'
import TitleV2 from '../../../analytics-details/work-mirror/TitleV2'
import GlLoader from '../../../share/GlLoader'
import { AuthorInterface } from '../../../../core/interface/author.interface'
import useAuthorAutoCompletionHook from '../../../analytics-details/work-mirror/author-autocomplete/useAuthorAutoCompletionHook'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { analyticDetailsV2StateAddAuthorInputAndGetAuthorRecoV3Action } from '../../../../store/actions/analytics-details/analytic-details-v2.actions'
import { resetGetAuthorRecoV3Status } from '../../../../store/reducers/analytics-details/analytic-details-v2.reducer'
import { getInitial } from '../../../../core/utils/get-author-initials'
import { selectAnalyticDetailsStateAnalyticDetailsAuthorsInput } from '../../../../store/getters/analytics-details/analytic-details-v2.getters'

const InputStyle = styled.input`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  border-radius: 4px;
  padding: 8px 28px 8px 8px;
  background-color: #ffffff33;
  height: 40px;
  color: #ffffff;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: Fira Sans, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  letter-spacing: 0.28px;

  ::placeholder {
    color: #b2b2b266;
  }

  :focus {
    outline: none;
  }
`

const AuthorAutoCompleteSelection = () => {
  const [displayInput, setDisplayInput] = useState<boolean>(false)
  const [needClose, setNeedClose] = useState<boolean>(false)
  const [query, setQuery] = useState<string>('')

  const inputRef = useRef<HTMLInputElement>(null)

  const authorsInput = useAppSelector(
    selectAnalyticDetailsStateAnalyticDetailsAuthorsInput,
  )

  const {
    state: {
      isLoadingGetAuthorsByQuery,
      getAuthorsByQuerySuccess,
      getAuthorsByQueryError,
      authors,
    },
    actions: { getAuthorsByQuery, resetAuthors },
  } = useAuthorAutoCompletionHook()

  const dispatch = useAppDispatch()

  const handleOnChangeQuery = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault()
    setQuery(event.target.value)
  }

  let timeGetBooksByQuery: NodeJS.Timeout | null = null

  const handleCallGetAuthorsByQuery = () => {
    timeGetBooksByQuery = setTimeout(() => {
      getAuthorsByQuery(query)
    }, 500)
  }

  useEffect(() => {
    if (query && query.length > 2) {
      handleCallGetAuthorsByQuery()
    }

    return () => {
      if (timeGetBooksByQuery) {
        clearTimeout(timeGetBooksByQuery)
      }
    }
  }, [query])

  useEffect(() => {
    if (needClose) {
      resetAuthors()
    }
  }, [needClose])

  useEffect(() => {
    if (inputRef?.current) {
      inputRef.current.focus()
    }
  }, [displayInput])

  const onClickItem = (id: string, is_profilable: boolean) => {
    if (id && is_profilable) {
      setQuery('')

      if (inputRef.current) {
        inputRef.current.value = ''
      }

      dispatch(
        analyticDetailsV2StateAddAuthorInputAndGetAuthorRecoV3Action({ id }),
      ).finally(() => {
        dispatch(resetGetAuthorRecoV3Status())
      })
    }
  }

  if (!displayInput) {
    return (
      <ButtonAddStyle
        type="button"
        className="flex flex-row gap-[8px] items-center justify-start"
        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
          event.preventDefault()
          setDisplayInput(true)
        }}
      >
        <span>{t('webApp.search.authors.addAuthor')}</span>
      </ButtonAddStyle>
    )
  }

  return (
    <div className="relative flex flex-col gap-[1px]">
      <div className="relative flex flex-col">
        {authorsInput.length < 3 && (
          <InputStyle
            placeholder="Rechercher"
            ref={inputRef}
            onChange={handleOnChangeQuery}
          />
        )}
        {query &&
          query.length > 2 &&
          (isLoadingGetAuthorsByQuery ||
            getAuthorsByQuerySuccess ||
            getAuthorsByQueryError) && (
            <MenuStyle
              className="absolute left-0 top-12 shadow-2xl flex flex-col"
              onMouseLeave={() => {
                setNeedClose(true)
              }}
              onMouseEnter={() => {
                setNeedClose(false)
              }}
            >
              <PerfectScrollbar className="max-h-[350px]">
                {(isLoadingGetAuthorsByQuery && (
                  <div className="flex-auto flex flex-col items-center justify-center w-full">
                    <GlLoader width={32} height={32} />
                  </div>
                )) || (
                  <div className="flex-auto flex flex-col">
                    {(authors.length &&
                      authors.map((author: AuthorInterface, index: number) => {
                        return (
                          <ItemButtonStyle
                            key={author.id}
                            isSelectable={author.is_profilable}
                            type="button"
                            className={`relative flex flex-row items-center justify-start gap-2 ${
                              index === 0
                                ? 'rounded-t-[4px]'
                                : index + 1 === authors.length
                                ? 'rounded-b-[4px]'
                                : ''
                            }`}
                            onClick={(event: React.MouseEvent) => {
                              event.preventDefault()
                              onClickItem(author.id, author.is_profilable)
                            }}
                            onMouseEnter={(
                              event: React.MouseEvent<HTMLButtonElement>,
                            ) => {
                              event.currentTarget.focus()
                            }}
                          >
                            <div className="p-2">
                              <div className="flex-auto flex flex-row items-center gap-1">
                                <AuthorInitialNameStyle>
                                  {getInitial(author.name)}
                                </AuthorInitialNameStyle>
                                <TitleV2 title={author?.name?.trim()} />
                              </div>
                            </div>
                          </ItemButtonStyle>
                        )
                      })) || (
                      <div className="flex flex-row items-center justify-center h-10">
                        <span>Pas de résultat</span>
                      </div>
                    )}
                  </div>
                )}
              </PerfectScrollbar>
            </MenuStyle>
          )}
      </div>
    </div>
  )
}

export default AuthorAutoCompleteSelection
